// kp-after-add.js: Add to cart never opens the cart drawer (A4, Eddie 2026-10-02: "we need to not prompt the drawer when
// add to cart is pressed"; the feedback he chose: "Button + cart badge").
//
// Every add path on this theme ends in the drawer's renderContents(): Dawn's product-form (PDP, quick-add modals, the
// pre-order confirm re-submit), the hub and search card handlers, kp-pick's flavor sheet, the pair module, the flavor flight,
// and the freebies / Futurola / custom-roll pages. Dawn's renderContents re-renders the drawer and then opens it. This file
// replaces that one method. It re-renders the drawer and the header count the same way, so the drawer is right when the
// shopper opens it and kp-cart-reconcile's observer still runs the Buy 3 Get 1 / gift rules on the add, but it never opens
// the drawer. Instead the pressed button reads "Added ✓" for 1.5 s, the header cart icon pulses once and a polite live
// region says "Added to cart".
// One path (the standard grid's quick-add modal) also calls open() itself 300 ms after rendering, so an open() with no
// trigger right after an add is ignored too. The header icon passes itself to open() and opens the drawer as before.
// window.kpAfterAdd(button) is the same feedback for any future path that adds without the drawer.
(() => {
  if (window.kpAfterAdd) return;
  const LABEL = 'Added ✓';
  const HOLD = 1500; // how long the button reads "Added ✓"
  const GUARD = 1500; // an open() with no trigger this soon after an add is the add path asking: ignored
  const PULSE = 500;
  const RECENT = 120000; // presses older than this aren't the add's button
  const QUICK_VIEW_CLOSE = 1000; // a quick view closes this long after its button reads "Added ✓"
  const PRESSABLE = 'button, input[type="submit"], input[type="button"], [role="button"], a.kph-atc, a.button';
  const ADDISH = /add|cart|bag|buy|bundle|pre-?order|pair|flight|select options|choose/i;
  const MODAL = 'quick-add-modal, dialog, [role="dialog"], [aria-modal="true"]';
  const CARD = '.product-card, .kph-card, .kpp__card, .card-wrapper, .grid__item, .product-item';
  const presses = [];
  let lastAdd = -Infinity;

  const style = document.createElement('style');
  style.id = 'kp-after-add-style';
  style.textContent = [
    '[data-kp-added]{-webkit-text-fill-color:transparent!important;text-shadow:none!important}',
    '[data-kp-added]>:not(.kp-added-label){visibility:hidden!important}',
    '.kp-added-label{position:absolute;top:0;right:0;bottom:0;left:0;display:flex;align-items:center;justify-content:center;',
    'margin:0!important;padding:0 .5em!important;pointer-events:none;white-space:nowrap;visibility:visible!important}',
    '#cart-icon-bubble.kp-cart-pulse{animation:kp-cart-pulse ' + PULSE + 'ms ease-out}',
    '@keyframes kp-cart-pulse{0%{transform:scale(1)}35%{transform:scale(1.2)}100%{transform:scale(1)}}',
    '@media (prefers-reduced-motion:reduce){#cart-icon-bubble.kp-cart-pulse{animation:none}}'
  ].join('');
  document.head.appendChild(style);

  // Which button the shopper pressed: the add paths don't all say, so remember recent presses.
  function note(el) {
    if (!(el instanceof Element)) return;
    presses.push({ el: el, t: Date.now() });
    if (presses.length > 10) presses.shift();
  }
  document.addEventListener('click', (event) => { if (event.target instanceof Element) note(event.target.closest(PRESSABLE)); }, true);
  document.addEventListener('submit', (event) => { if (event.submitter) note(event.submitter); }, true);

  const visible = (el) => el.isConnected && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  const words = (el) => ((el.getAttribute('aria-label') || '') + ' ' + (el.tagName === 'INPUT' ? el.value : el.textContent)).replace(/\s+/g, ' ');
  const NOT_ADD = /wish|favou?rite|save for later|notify|remove|quick view/i; // "Add to wishlist" is not an add to cart
  const usable = (el) => visible(el) && !el.closest('cart-drawer, #cart-icon-bubble') && ADDISH.test(words(el)) && !NOT_ADD.test(words(el));
  // The most recent add-type button that is still on screen. A sheet or modal that closed on the add hides its own button,
  // so the label lands on the card or PDP button that opened it; a quick view opened from a card's icon shows it on that
  // card's add button.
  function pressed(hint) {
    const now = Date.now();
    const list = presses.filter((entry) => now - entry.t < RECENT).map((entry) => entry.el).reverse();
    if (hint instanceof Element && hint.matches(PRESSABLE)) list.unshift(hint);
    const direct = list.find(usable);
    if (direct) return direct;
    for (const el of list) {
      const card = el.isConnected && !el.closest(MODAL) ? el.closest(CARD) : null;
      const button = card && Array.prototype.find.call(card.querySelectorAll(PRESSABLE), usable);
      if (button) return button;
    }
    return null;
  }

  function flash(button) {
    if (!button) return;
    if (button.tagName === 'INPUT') {
      if (button.dataset.kpAddedValue === undefined) button.dataset.kpAddedValue = button.value;
      button.value = LABEL;
      clearTimeout(button.kpAddedTimer);
      button.kpAddedTimer = setTimeout(() => { button.value = button.dataset.kpAddedValue; delete button.dataset.kpAddedValue; }, HOLD);
      return;
    }
    if (button.kpAdded) { clearTimeout(button.kpAdded.timer); button.kpAdded.timer = setTimeout(button.kpAdded.end, HOLD); return; }
    const computed = getComputedStyle(button);
    let color = computed.color;
    if (color === 'transparent' || /rgba\([^)]*,\s*0\)$/.test(color)) {
      const child = Array.prototype.find.call(button.children, (node) => node.getClientRects().length);
      color = child ? getComputedStyle(child).color : '';
    }
    const label = document.createElement('span');
    label.className = 'kp-added-label';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = LABEL;
    if (color) {
      label.style.setProperty('color', color, 'important');
      label.style.setProperty('-webkit-text-fill-color', color, 'important');
    }
    const relative = computed.position === 'static';
    if (relative) button.style.position = 'relative';
    button.setAttribute('data-kp-added', '');
    button.appendChild(label);
    // A path that redraws its own button after the add (the flavor flight does) would drop the label: put it back.
    const keep = new MutationObserver(() => { if (button.kpAdded && !label.isConnected) button.appendChild(label); });
    keep.observe(button, { childList: true });
    const end = () => {
      keep.disconnect();
      label.remove();
      button.removeAttribute('data-kp-added');
      if (relative) button.style.position = '';
      delete button.kpAdded;
    };
    button.kpAdded = { end: end, timer: setTimeout(end, HOLD) };
  }

  function pulse() {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;
    icon.classList.remove('kp-cart-pulse');
    void icon.offsetWidth;
    icon.classList.add('kp-cart-pulse');
    setTimeout(() => icon.classList.remove('kp-cart-pulse'), PULSE + 50);
  }

  let live = null;
  function announce() {
    if (!live) {
      live = document.createElement('div');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.style.cssText = 'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0';
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(() => { live.textContent = 'Added to cart'; }, 60);
  }

  window.kpAfterAdd = function (button) {
    lastAdd = Date.now();
    // After the add path's own finally (several restore their button there), so the label isn't wiped straight away.
    setTimeout(() => {
      const target = pressed(button);
      flash(target);
      pulse();
      announce();
      // A quick view the add came from closes once "Added ✓" has shown, as Dawn's quick-add does on an add. The hub
      // card's quick view used to stay open behind the drawer; left open now, it would keep the page locked.
      const quickView = target && target.closest('quick-add-modal[open]');
      if (quickView && typeof quickView.hide === 'function') {
        setTimeout(() => { if (quickView.hasAttribute('open')) quickView.hide(); }, QUICK_VIEW_CLOSE);
      }
      // A modal that closes a beat after rendering (the standard grid's quick-add modal waits 300 ms) hides its button.
      if (target && target.closest(MODAL)) {
        setTimeout(() => {
          if (visible(target)) return;
          if (target.kpAdded) target.kpAdded.end();
          flash(pressed(null));
        }, 450);
      }
    }, 0);
  };

  // Dawn's renderContents without the open(): the drawer and the header count, from the add's own sections.
  function render(drawer, state) {
    const inner = drawer.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    drawer.productId = state.id;
    drawer.getSectionsToRender().forEach((section) => {
      const html = state.sections && state.sections[section.id];
      const node = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (node && html) node.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
    });
    // The re-render replaces the overlay; Dawn binds the new one when it opens, so bind it here (once per element).
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.kpBound) {
      overlay.addEventListener('click', drawer.close.bind(drawer));
      overlay.kpBound = true;
    }
  }
  async function fetchSections() {
    // no-store: the same URL rendered before the add can come back from the browser cache with the old cart. No Accept
    // header: with "application/json" Shopify answers a collection URL with the collection's JSON instead of the sections.
    const response = await fetch(location.pathname + '?sections=cart-drawer,cart-icon-bubble', { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart sections failed: ' + response.status);
    return response.json();
  }

  function patch(Drawer) {
    const proto = Drawer.prototype;
    if (proto.kpAfterAdd) return;
    proto.kpAfterAdd = true;
    const nativeOpen = proto.open;
    proto.renderContents = function (state) {
      lastAdd = Date.now();
      const hint = this.activeElement;
      if (state && state.sections && state.sections['cart-drawer']) {
        render(this, state);
        window.kpAfterAdd(hint);
        return;
      }
      // A caller that hands over /cart.js data without sections (the standard grid's quick-add modal): fetch them.
      fetchSections()
        .then((sections) => render(this, { id: state && state.id, sections: sections }))
        .catch((error) => console.error('kpAfterAdd:', error))
        .then(() => window.kpAfterAdd(hint));
    };
    proto.open = function (triggeredBy) {
      if (!triggeredBy && Date.now() - lastAdd < GUARD) return;
      return nativeOpen.apply(this, arguments);
    };
  }
  if (window.customElements) customElements.whenDefined('cart-drawer').then(() => patch(customElements.get('cart-drawer')));
})();
